import { styled } from 'styled-components';
import { lighten } from 'polished';
import { useSelector } from '@/redux';
import { Star } from '@/components/uielements/icon/star';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { Render } from '@/components/uielements/render';
import { isDark } from '@/utils/global';
import { supportsLoyalty } from '@/utils/loyalty';

interface SignupBonusBadgeProps {
  style?: React.CSSProperties;
}

export const SignupBonusBadge = ({ style }: SignupBonusBadgeProps) => {
  const organization = useSelector((state) => state.app.organization);
  const signupBonusPoints = supportsLoyalty(organization)
    ? organization.loyalty.signUpBonusPoints
    : 0;

  return (
    <Render condition={Boolean(supportsLoyalty(organization) && signupBonusPoints)}>
      <StyledBadge style={style}>
        <StyledBackgroundStar>
          <Star
            title="Loyalty Star Icon"
            style={{ color: '#fff', width: '18px', height: '18px' }}
          />
        </StyledBackgroundStar>
        <StyledBadgeContent>
          <StyledBadgeTitle>
            Sign up now and earn {signupBonusPoints} points instantly!
          </StyledBadgeTitle>
        </StyledBadgeContent>
      </StyledBadge>
    </Render>
  );
};

const StyledBadge = styled.div`
  p {
    margin: 0;
  }
  display: flex;
  gap: 8px;
  align-items: center;
  background-color: ${({ theme }) => {
    const isDarkBg = theme.checkout?.background_color && isDark(theme.checkout.background_color);
    const primaryColor = theme.buttons.primary_button_background_color;
    return isDarkBg ? lighten(0.1, primaryColor) : lighten(0.38, primaryColor);
  }};
  border-radius: 8px;
  padding: 12px 12px;
  border: 1px solid ${({ theme }) => theme.buttons.primary_button_border_color};
  margin-bottom: 16px;
  justify-content: center;
`;

const StyledBackgroundStar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.buttons.primary_button_background_color};
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
`;

const StyledBadgeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StyledBadgeTitle = styled.p`
  color: ${({ theme }) => {
    // Use a darker shade of the primary color for better readability
    const primaryColor = theme.buttons.primary_button_background_color;
    const textColor = theme.checkout?.secondary_font
      ? getFontStyles(theme.checkout.secondary_font).color
      : theme.global.body_color;
    const isDarkBg = theme.checkout?.background_color && isDark(theme.checkout.background_color);
    return isDarkBg ? textColor : primaryColor;
  }};
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
`;
